import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type DatePreset =
  | "this_month"
  | "this_quarter"
  | "this_year"
  | "last_week"
  | "last_7_days"
  | "last_30_days"
  | "last_90_days"
  | "last_month"
  | "last_quarter"
  | "last_year"
  | "next_month"
  | "next_quarter"
  | "custom"
  | "";

/** Every preset in display order — single list for the pages that whitelist presets. */
export const ALL_DATE_PRESETS: readonly DatePreset[] = [
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
  "last_year",
  "last_week",
  "next_month",
  "next_quarter",
  "custom",
];

export interface DateRangeResult {
  from: string;
  to: string;
}

function toYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function computeDateRange(
  preset: DatePreset,
  customFrom = "",
  customTo = "",
  now = new Date(),
): DateRangeResult {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "this_month": {
      const y = today.getFullYear();
      const m = today.getMonth();
      return { from: toYMD(new Date(y, m, 1)), to: toYMD(new Date(y, m + 1, 0)) };
    }
    case "this_quarter": {
      const y = today.getFullYear();
      const startMonth = Math.floor(today.getMonth() / 3) * 3;
      return {
        from: toYMD(new Date(y, startMonth, 1)),
        to: toYMD(new Date(y, startMonth + 3, 0)),
      };
    }
    case "this_year": {
      const y = today.getFullYear();
      return { from: toYMD(new Date(y, 0, 1)), to: toYMD(new Date(y, 11, 31)) };
    }
    case "last_year": {
      const y = today.getFullYear() - 1;
      return { from: toYMD(new Date(y, 0, 1)), to: toYMD(new Date(y, 11, 31)) };
    }
    case "last_week": {
      // Monday-start: dayOfWeek 0=Mon … 6=Sun
      const dow = (today.getDay() + 6) % 7;
      const lastMon = addDays(today, -(dow + 7));
      const lastSun = addDays(today, -(dow + 1));
      return { from: toYMD(lastMon), to: toYMD(lastSun) };
    }
    // Rolling windows, inclusive of today — "last 7 days" = today plus the 6
    // days before it, matching the relative 7d/30d/90d filters elsewhere.
    case "last_7_days":
      return { from: toYMD(addDays(today, -6)), to: toYMD(today) };
    case "last_30_days":
      return { from: toYMD(addDays(today, -29)), to: toYMD(today) };
    case "last_90_days":
      return { from: toYMD(addDays(today, -89)), to: toYMD(today) };
    case "last_month": {
      const y = today.getFullYear();
      const m = today.getMonth(); // 0-indexed current month
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0); // day-0 of current month = last day of prev month
      return { from: toYMD(start), to: toYMD(end) };
    }
    case "last_quarter": {
      const y = today.getFullYear();
      const currentQ = Math.floor(today.getMonth() / 3); // 0=Q1,1=Q2,2=Q3,3=Q4
      const prevQ = (currentQ + 3) % 4;
      const prevQYear = currentQ === 0 ? y - 1 : y;
      const startMonth = prevQ * 3;
      const start = new Date(prevQYear, startMonth, 1);
      const end = new Date(prevQYear, startMonth + 3, 0);
      return { from: toYMD(start), to: toYMD(end) };
    }
    case "next_month": {
      const y = today.getFullYear();
      const m = today.getMonth();
      const start = new Date(y, m + 1, 1);
      const end = new Date(y, m + 2, 0);
      return { from: toYMD(start), to: toYMD(end) };
    }
    case "next_quarter": {
      const y = today.getFullYear();
      const currentQ = Math.floor(today.getMonth() / 3);
      const nextQ = (currentQ + 1) % 4;
      const nextQYear = currentQ === 3 ? y + 1 : y;
      const startMonth = nextQ * 3;
      const start = new Date(nextQYear, startMonth, 1);
      const end = new Date(nextQYear, startMonth + 3, 0);
      return { from: toYMD(start), to: toYMD(end) };
    }
    case "custom":
      return { from: customFrom, to: customTo };
    default:
      return { from: "", to: "" };
  }
}

export function getPresetLabel(preset: DatePreset): string {
  switch (preset) {
    case "this_month": return "This month";
    case "this_quarter": return "This quarter";
    case "this_year": return "This year";
    case "last_week": return "Last week";
    case "last_7_days": return "Last 7 days";
    case "last_30_days": return "Last 30 days";
    case "last_90_days": return "Last 90 days";
    case "last_month": return "Last month";
    case "last_quarter": return "Last quarter";
    case "last_year": return "Last year";
    case "next_month": return "Next month";
    case "next_quarter": return "Next quarter";
    case "custom": return "Custom";
    default: return "All dates";
  }
}

interface DateRangeFilterProps {
  preset: DatePreset;
  customFrom?: string;
  customTo?: string;
  onPresetChange: (preset: DatePreset) => void;
  onCustomFromChange?: (v: string) => void;
  onCustomToChange?: (v: string) => void;
  triggerClassName?: string;
  /** Restrict the dropdown to a subset of presets (display order preserved). */
  presets?: readonly DatePreset[];
  /** Hide the "All dates" (empty preset) option for pages that always need a bounded range. */
  allowAll?: boolean;
}

export function DateRangeFilter({
  preset,
  customFrom = "",
  customTo = "",
  onPresetChange,
  onCustomFromChange,
  onCustomToChange,
  triggerClassName = "w-40 text-sm",
  presets = ALL_DATE_PRESETS,
  allowAll = true,
}: DateRangeFilterProps) {
  return (
    <>
      <Select value={preset || "__all__"} onValueChange={(v) => onPresetChange(v === "__all__" ? "" : (v as DatePreset))}>
        <SelectTrigger className={triggerClassName} data-testid="select-date-range-preset">
          <SelectValue placeholder="All dates" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="__all__">All dates</SelectItem>}
          {presets.map((p) =>
            p === "custom" ? (
              <SelectItem key={p} value="custom">Custom…</SelectItem>
            ) : (
              <SelectItem key={p} value={p}>{getPresetLabel(p)}</SelectItem>
            ),
          )}
        </SelectContent>
      </Select>

      {preset === "custom" && (
        <>
          <input
            type="date"
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white h-9"
            value={customFrom}
            onChange={(e) => onCustomFromChange?.(e.target.value)}
            placeholder="From"
            data-testid="input-date-range-from"
          />
          <input
            type="date"
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white h-9"
            value={customTo}
            onChange={(e) => onCustomToChange?.(e.target.value)}
            placeholder="To"
            data-testid="input-date-range-to"
          />
        </>
      )}
    </>
  );
}
